import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Search as SearchIcon, MapPin, X, Wrench } from "lucide-react";
import { VendorCard } from "./Home";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Search() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [city, setCity] = useState(params.get("city") || "");
  const [category, setCategory] = useState(params.get("category") || "");
  const [categories, setCategories] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/categories`).then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    if (city) qs.set("city", city);
    if (category) qs.set("category", category);
    axios
      .get(`${API}/vendors/search?${qs.toString()}`, { signal: ctrl.signal })
      .then((r) => setResults(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    setParams(qs, { replace: true });
    return () => ctrl.abort();
    // eslint-disable-next-line
  }, [q, city, category]);

  const clear = () => { setQ(""); setCity(""); setCategory(""); };

  return (
    <div className="px-6 sm:px-12 lg:px-24 py-12">
      <div className="mb-10">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600 mb-3">// Vendor Directory</div>
        <h1 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl text-slate-50">
          Find your <span className="text-orange-500">contractor</span>
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Filters */}
        <aside className="lg:col-span-1 space-y-6">
          <div className="bg-slate-900 border border-slate-800 p-6 rounded-sm">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600 mb-4">Search</h3>
            <div className="relative mb-4">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                data-testid="search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Name or keyword..."
                className="w-full pl-9 pr-3 py-2.5 bg-slate-950 border border-slate-700 text-slate-50 placeholder:text-slate-500 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none rounded-sm text-sm"
              />
            </div>
            <div className="relative mb-4">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                data-testid="search-city-input"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="City..."
                className="w-full pl-9 pr-3 py-2.5 bg-slate-950 border border-slate-700 text-slate-50 placeholder:text-slate-500 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none rounded-sm text-sm"
              />
            </div>
            {(q || city || category) && (
              <button onClick={clear} data-testid="search-clear" className="w-full inline-flex items-center justify-center gap-2 py-2 text-xs uppercase tracking-wider font-bold text-slate-400 hover:text-orange-500 border border-slate-700 hover:border-orange-500 rounded-sm">
                <X className="w-3 h-3" /> Clear all
              </button>
            )}
          </div>

          <div className="bg-slate-900 border border-slate-800 p-6 rounded-sm">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600 mb-4">Category</h3>
            <div className="space-y-1 max-h-96 overflow-y-auto">
              <button
                onClick={() => setCategory("")}
                className={`w-full text-left text-sm py-2 px-3 rounded-sm transition-colors ${!category ? "bg-orange-600/20 text-orange-500 font-semibold" : "text-slate-400 hover:text-slate-100"}`}
              >
                All categories
              </button>
              {categories.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setCategory(c.name)}
                  data-testid={`filter-cat-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`w-full text-left text-sm py-2 px-3 rounded-sm transition-colors ${category === c.name ? "bg-orange-600/20 text-orange-500 font-semibold" : "text-slate-400 hover:text-slate-100"}`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Results */}
        <div className="lg:col-span-3">
          <div className="flex items-center justify-between mb-6">
            <div className="text-sm text-slate-400 font-mono">
              {loading ? "Searching..." : `${results.length} vendor${results.length === 1 ? "" : "s"} found`}
            </div>
          </div>
          {results.length === 0 && !loading ? (
            <div className="bg-slate-900 border border-slate-800 p-16 text-center rounded-sm">
              <Wrench className="w-12 h-12 text-slate-700 mx-auto mb-4" />
              <h3 className="font-display font-bold text-slate-200 text-xl mb-2">No vendors found</h3>
              <p className="text-slate-500 text-sm">Try adjusting your filters or search keywords.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {results.map((v) => <VendorCard key={v.id} v={v} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
